import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useT } from "@/lib/translation";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  linkApple,
  linkGoogle,
  setPassword,
  unlinkApple,
  unlinkGoogle,
} from "@/api/client";
import { getAppleCredential } from "@/lib/appleSignIn";

interface GoogleSecurityCardProps {
  isGoogleLinked: boolean;
  isAppleLinked: boolean;
  hasPassword: boolean;
}

export default function GoogleSecurityCard({
  isGoogleLinked,
  isAppleLinked,
  hasPassword,
}: GoogleSecurityCardProps) {
  const t = useT();
  const queryClient = useQueryClient();
  const [isLinking, setIsLinking] = useState(false);
  const [isUnlinking, setIsUnlinking] = useState(false);
  const [isLinkingApple, setIsLinkingApple] = useState(false);
  const [isUnlinkingApple, setIsUnlinkingApple] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [forcedBeforeUnlink, setForcedBeforeUnlink] = useState<"google" | "apple" | null>(null);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSavingPassword, setIsSavingPassword] = useState(false);

  useEffect(() => {
    const scriptId = "google-gsi-client";
    let script = document.getElementById(scriptId) as HTMLScriptElement;

    if (!script) {
      script = document.createElement("script");
      script.id = scriptId;
      script.src = "https://accounts.google.com/gsi/client";
      script.async = true;
      script.defer = true;
      document.body.appendChild(script);
    }
  }, []);

  const refetchSettings = () =>
    queryClient
      .invalidateQueries({ queryKey: ["userSettings"] })
      .catch(() => {});

  const resetPasswordForm = () => {
    setShowPasswordForm(false);
    setForcedBeforeUnlink(null);
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
  };

  const handleLinkGoogle = () => {
    // TECH-DEBT: any — window.google and the GSI callback/notification types should use
    // the @types/google.accounts package or a local declaration file instead of `as any`.
    const google = (
      window as Window & {
        google?: {
          accounts?: {
            id?: {
              initialize: (cfg: unknown) => void;
              prompt: (fn: unknown) => void;
            };
          };
        };
      }
    ).google;
    if (!google?.accounts?.id) {
      toast.error(t("settings:toast.error"), {
        description: t("settings:security.toast.googleStillLoading"),
      });
      return;
    }

    setIsLinking(true);
    google.accounts.id.initialize({
      client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID || "",
      // Always show the account chooser, never silently reuse the last session.
      auto_select: false,
      callback: async (response: { credential: string }) => {
        try {
          await linkGoogle(response.credential);
          toast.success(t("settings:toast.success"), {
            description: t("settings:security.toast.googleLinked"),
          });
          refetchSettings();
        } catch (err: unknown) {
          toast.error(t("settings:toast.error"), {
            description:
              err instanceof Error
                ? err.message
                : t("settings:security.toast.linkFailed"),
          });
        } finally {
          setIsLinking(false);
        }
      },
    });
    google.accounts.id.prompt(
      (notification: {
        isNotDisplayed?: () => boolean;
        isSkippedMoment?: () => boolean;
      }) => {
        if (
          notification?.isNotDisplayed?.() ||
          notification?.isSkippedMoment?.()
        ) {
          setIsLinking(false);
          toast.error(t("settings:toast.error"), {
            description: t("settings:security.toast.promptBlocked"),
          });
        }
      },
    );
  };

  const handleUnlinkGoogle = async () => {
    setIsUnlinking(true);
    try {
      await unlinkGoogle();
      toast.success(t("settings:toast.success"), {
        description: t("settings:security.toast.googleUnlinked"),
      });
      refetchSettings();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "";
      if (message === "PASSWORD_REQUIRED") {
        setForcedBeforeUnlink("google");
        setShowPasswordForm(true);
      } else {
        toast.error(t("settings:toast.error"), {
          description: message || t("settings:security.toast.unlinkFailed"),
        });
      }
    } finally {
      setIsUnlinking(false);
    }
  };

  const handleLinkApple = async () => {
    setIsLinkingApple(true);
    try {
      const credential = await getAppleCredential();
      if (!credential) return;
      await linkApple(credential);
      toast.success(t("settings:toast.success"), {
        description: t("settings:security.toast.appleLinked"),
      });
      refetchSettings();
    } catch (err: unknown) {
      toast.error(t("settings:toast.error"), {
        description:
          err instanceof Error
            ? err.message
            : t("settings:security.toast.appleLinkFailed"),
      });
    } finally {
      setIsLinkingApple(false);
    }
  };

  const handleUnlinkApple = async () => {
    setIsUnlinkingApple(true);
    try {
      await unlinkApple();
      toast.success(t("settings:toast.success"), {
        description: t("settings:security.toast.appleUnlinked"),
      });
      refetchSettings();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "";
      if (message === "PASSWORD_REQUIRED") {
        setForcedBeforeUnlink("apple");
        setShowPasswordForm(true);
      } else {
        toast.error(t("settings:toast.error"), {
          description: message || t("settings:security.toast.appleUnlinkFailed"),
        });
      }
    } finally {
      setIsUnlinkingApple(false);
    }
  };

  const handleSavePassword = async () => {
    if (newPassword !== confirmPassword) {
      toast.error(t("settings:toast.error"), {
        description: t("settings:security.toast.passwordMismatch"),
      });
      return;
    }

    setIsSavingPassword(true);
    try {
      await setPassword(newPassword, hasPassword ? currentPassword : undefined);
      toast.success(t("settings:toast.success"), {
        description: hasPassword
          ? t("settings:security.toast.passwordChanged")
          : t("settings:security.toast.passwordSet"),
      });
      const wasForced = forcedBeforeUnlink;
      resetPasswordForm();
      refetchSettings();

      if (wasForced === "apple") {
        await unlinkApple();
        toast.success(t("settings:toast.success"), {
          description: t("settings:security.toast.appleUnlinked"),
        });
        refetchSettings();
      } else if (wasForced === "google") {
        await unlinkGoogle();
        toast.success(t("settings:toast.success"), {
          description: t("settings:security.toast.googleUnlinked"),
        });
        refetchSettings();
      }
    } catch (err: unknown) {
      toast.error(t("settings:toast.error"), {
        description:
          err instanceof Error
            ? err.message
            : t("settings:security.toast.passwordSaveFailed"),
      });
    } finally {
      setIsSavingPassword(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("settings:security.title")}</CardTitle>
        <CardDescription>{t("settings:security.description")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-sm font-medium">
              {t("settings:security.googleAccount")}
            </p>
            <p className="text-xs text-muted-foreground">
              {isGoogleLinked
                ? t("settings:security.linked")
                : t("settings:security.notLinked")}
            </p>
          </div>
          {isGoogleLinked ? (
            <Button
              type="button"
              variant="outline"
              className="w-40"
              onClick={handleUnlinkGoogle}
              disabled={isUnlinking}
            >
              {isUnlinking
                ? t("settings:security.unlinking")
                : t("settings:security.unlinkGoogle")}
            </Button>
          ) : (
            <Button
              type="button"
              variant="outline"
              className="w-40"
              onClick={handleLinkGoogle}
              disabled={isLinking}
            >
              {isLinking
                ? t("settings:security.linking")
                : t("settings:security.linkGoogle")}
            </Button>
          )}
        </div>

        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-sm font-medium">
              {t("settings:security.appleAccount")}
            </p>
            <p className="text-xs text-muted-foreground">
              {isAppleLinked
                ? t("settings:security.linked")
                : t("settings:security.notLinked")}
            </p>
          </div>
          {isAppleLinked ? (
            <Button
              type="button"
              variant="outline"
              className="w-40"
              onClick={handleUnlinkApple}
              disabled={isUnlinkingApple}
            >
              {isUnlinkingApple
                ? t("settings:security.unlinking")
                : t("settings:security.unlinkApple")}
            </Button>
          ) : (
            <Button
              type="button"
              variant="outline"
              className="w-40"
              onClick={handleLinkApple}
              disabled={isLinkingApple}
            >
              {isLinkingApple
                ? t("settings:security.linking")
                : t("settings:security.linkApple")}
            </Button>
          )}
        </div>

        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-sm font-medium">
              {t("settings:security.password")}
            </p>
            <p className="text-xs text-muted-foreground">
              {hasPassword
                ? t("settings:security.hasPassword")
                : t("settings:security.noPassword")}
            </p>
          </div>
          {!showPasswordForm && (
            <Button
              type="button"
              variant="outline"
              className="w-40"
              onClick={() => setShowPasswordForm(true)}
            >
              {hasPassword
                ? t("settings:security.changePassword")
                : t("settings:security.setPassword")}
            </Button>
          )}
        </div>

        {showPasswordForm && (
          <div className="grid gap-3 rounded-md border p-3">
            {forcedBeforeUnlink && (
              <p className="text-xs text-muted-foreground">
                {t(
                  forcedBeforeUnlink === "apple"
                    ? "settings:security.needPasswordBeforeUnlinkApple"
                    : "settings:security.needPasswordBeforeUnlink",
                )}
              </p>
            )}
            {hasPassword && (
              <div className="grid gap-2">
                <Label htmlFor="security-current-password">
                  {t("settings:security.currentPassword")}
                </Label>
                <Input
                  id="security-current-password"
                  type="password"
                  maxLength={72}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  disabled={isSavingPassword}
                />
              </div>
            )}
            <div className="grid gap-2">
              <Label htmlFor="security-new-password">
                {t("settings:security.newPassword")}
              </Label>
              <Input
                id="security-new-password"
                type="password"
                maxLength={72}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={isSavingPassword}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="security-confirm-password">
                {t("settings:security.confirmPassword")}
              </Label>
              <Input
                id="security-confirm-password"
                type="password"
                maxLength={72}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={isSavingPassword}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={resetPasswordForm}
                disabled={isSavingPassword}
              >
                {t("common:actions.cancel")}
              </Button>
              <Button
                type="button"
                onClick={handleSavePassword}
                disabled={isSavingPassword || !newPassword || !confirmPassword}
              >
                {isSavingPassword
                  ? t("settings:security.saving")
                  : t("settings:security.savePassword")}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
